import ReportHeader from "@/components/ReportHeader";
import { errorHandler, formatDateOnly, formatToCurrency } from "@/lib/utils";
import {
  ExpensesReportItemResponse,
  expensesReportService,
} from "@/services/api/expensesReport";
import {
  AlertTriangle,
  DollarSign,
  FileText,
  Fuel,
  Hammer,
  Shield,
  TrendingUp,
  Wrench,
} from "lucide-react";
import React from "react";
import ExpenseCategoryCard from "./ExpenseCategoryCard";
import TotalExpensesCard from "./TotalExpensesCard";
import VehicleExpenseItem from "./VehicleExpenseItem";

interface ExpensesReportTableProps {
  data: {
    [key: string]: ExpensesReportItemResponse;
  };
  startDate: string;
  endDate: string;
  expenseCategories: {
    id: string;
    label: string;
  }[];
}

const extractTotals = (data: { [key: string]: ExpensesReportItemResponse }) => {
  const totals = {
    fuelling: 0,
    licensing: 0,
    maintenance: 0,
    ticket: 0,
    otherFees: 0,
    repair: 0,
    insurance: 0,
    totalExpenses: 0,
    totalRevenues: 0,
    netResult: 0,
  };

  Object.values(data).forEach((vehicleExpense) => {
    totals.fuelling += vehicleExpense.categories.fuelling?.total || 0;
    totals.licensing += vehicleExpense.categories.licensing?.total || 0;
    totals.maintenance += vehicleExpense.categories.maintenance?.total || 0;
    totals.ticket += vehicleExpense.categories.ticket?.total || 0;
    totals.otherFees += vehicleExpense.categories.otherFees?.total || 0;
    totals.repair += vehicleExpense.categories.repair?.total || 0;
    totals.insurance += vehicleExpense.categories.insurance?.total || 0;
    totals.totalRevenues += vehicleExpense.revenues?.total || 0;
  });

  totals.totalExpenses = Object.values(data).reduce(
    (sum, vehicle) => sum + (vehicle.total || 0),
    0,
  );

  totals.netResult = totals.totalRevenues - totals.totalExpenses;

  return totals;
};

const ExpensesReportTable: React.FC<ExpensesReportTableProps> = ({
  data,
  startDate,
  endDate,
  expenseCategories,
}) => {
  const [expandedVehicles, setExpandedVehicles] = React.useState<number[]>([]);
  const [expandedCategories, setExpandedCategories] = React.useState<{
    [key: string]: string[];
  }>({});
  const [pdfLoading, setPdfLoading] = React.useState(false);

  const formattedStartDate = formatDateOnly(startDate);
  const formattedEndDate = formatDateOnly(endDate);

  const totals = extractTotals(data);

  const handleExportPdf = async () => {
    setPdfLoading(true);
    try {
      const params = {
        vehicle_id: Object.keys(data).join(","),
        start_date: startDate,
        end_date: endDate,
        categories: expenseCategories.map((cat) => cat.id).join(","),
      };

      const response = await expensesReportService.getExpensesPdfReport(params);

      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl);
    } catch (error) {
      errorHandler(error, "Erro ao exportar PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const getCategoryLabel = (categoryId: string) => {
    const category = expenseCategories.find((cat) => cat.id === categoryId);
    return category ? category.label : categoryId;
  };

  const toggleVehicleExpansion = (vehicleId: number) => {
    setExpandedVehicles((prev) =>
      prev.includes(vehicleId)
        ? prev.filter((id) => id !== vehicleId)
        : [...prev, vehicleId],
    );
  };

  const toggleCategoryExpansion = (vehicleId: number, category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [vehicleId]: prev[vehicleId]?.includes(category)
        ? prev[vehicleId].filter((cat) => cat !== category)
        : [...(prev[vehicleId] || []), category],
    }));
  };

  return (
    <div className="space-y-4">
      <ReportHeader
        primaryInfo="Relatório de Despesas"
        secondaryInfo={`De ${formattedStartDate} a ${formattedEndDate}`}
        handleExportPdf={handleExportPdf}
        loading={pdfLoading}
      />

      {/* Cartões de categorias */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-7 text-sm">
        {expenseCategories.map((category) => (
          <ExpenseCategoryCard
            key={category.id}
            icon={
              category.id === "fuelling" ? (
                <Fuel className="text-blue-500" />
              ) : category.id === "licensing" ? (
                <FileText className="text-blue-500" />
              ) : category.id === "maintenance" ? (
                <Wrench className="text-blue-500" />
              ) : category.id === "ticket" ? (
                <AlertTriangle className="text-blue-500" />
              ) : category.id === "otherFees" ? (
                <DollarSign className="text-blue-500" />
              ) : category.id === "repair" ? (
                <Hammer className="text-blue-500" />
              ) : category.id === "insurance" ? (
                <Shield className="text-blue-500" />
              ) : null
            }
            label={category.label}
            total={totals[category.id as keyof typeof totals]}
          />
        ))}
      </div>

      {/* Cartão de receitas totais */}
      <div className="bg-card p-4 rounded-lg shadow-sm text-center border border-border">
        <div className="flex items-center justify-center space-x-2">
          <TrendingUp className="text-green-500" />
          <span className="font-medium text-foreground">Total de Receitas</span>
        </div>
        <div className="text-2xl font-bold mt-3 text-green-600 dark:text-green-400 bg-background rounded-lg p-2">
          {formatToCurrency(totals.totalRevenues)}
        </div>
      </div>

      {/* Cartão de valor total despesas */}
      <TotalExpensesCard totalExpenses={totals.totalExpenses} />

      {/* Resultado líquido */}
      <div className="bg-card p-4 rounded-lg shadow-sm text-center border border-border">
        <div className="flex items-center justify-center space-x-2">
          <span className="font-medium text-foreground">Resultado Líquido</span>
          <span className="text-xs text-muted-foreground">
            (Receitas − Despesas)
          </span>
        </div>
        <div
          className={`text-2xl font-bold mt-3 bg-background rounded-lg p-2 ${
            totals.netResult >= 0
              ? "text-green-600 dark:text-green-400"
              : "text-red-600 dark:text-red-400"
          }`}
        >
          {formatToCurrency(totals.netResult)}
        </div>
      </div>

      {/* Lista de veículos */}
      {Object.values(data).map((vehicleExpense) => (
        <VehicleExpenseItem
          key={vehicleExpense.vehicle.id}
          vehicle={{
            id: vehicleExpense.vehicle.id,
            title: vehicleExpense.vehicle.title,
            plate: vehicleExpense.vehicle.plate,
          }}
          total={vehicleExpense.total}
          isExpanded={expandedVehicles.includes(vehicleExpense.vehicle.id)}
          onToggleExpansion={() =>
            toggleVehicleExpansion(vehicleExpense.vehicle.id)
          }
          categories={vehicleExpense.categories}
          revenues={vehicleExpense.revenues}
          net_result={vehicleExpense.net_result}
          expandedCategories={
            expandedCategories[vehicleExpense.vehicle.id] || []
          }
          onToggleCategoryExpansion={(category) =>
            toggleCategoryExpansion(vehicleExpense.vehicle.id, category)
          }
          getCategoryLabel={getCategoryLabel}
          expenseCategories={expenseCategories}
        />
      ))}
    </div>
  );
};

export default ExpensesReportTable;
